// src/components/widgets/AuthModal/AuthModal.jsx
//
// ВХОД И РЕГИСТРАЦИЯ ОДНОЙ МОДАЛКОЙ, БЕЗ УХОДА СО СТРАНИЦЫ.
//
// ═══════════════════════════════════════════════════════════════════
// 🔑 ЗАЧЕМ (решение владельца 2026-10-29, «отлично, всё делаем»).
//
//    Было: на витрине стикеров кнопки «Загрузить свою картинку» и
//    «Создать подборку» вели на /register. Человек уходил со страницы
//    со стикерами — то есть ТЕРЯЛ то, ради чего пришёл (контекст,
//    выбранную карточку, подборку). Владелец: «нужно сделать так, чтобы
//    мы возвращались на страницу с которой мы регистрировались, либо
//    было бы круто, если бы регистрация была бы просто в модальных
//    окнах на тех же страницах».
//
//    ⚠️ ПЕРЕХОД И ПОТЕРЯ КОНТЕКСТА — только часть беды. Была вторая,
//    скрытая: после регистрации токен не сохранялся вовсе (`authSlice`),
//    поэтому человек «зарегистрировался» и остался гостем. Исправлено
//    там же; здесь модалку закрываем только если вход действительно
//    произошёл — иначе показываем честную ошибку.
//
// 🔑 ЧЕМ ОТЛИЧАЕТСЯ ОТ СТАРОГО `LoginWidget`.
//    * стиль витрины стикеров (Manrope + Golos Text, токены проекта),
//      а не общая страничная модалка;
//    * две вкладки «Регистрация / Вход» — без перехода на другие адреса;
//    * галочка согласия на ПДн ТОЛЬКО при регистрации (при входе новых
//      данных не собирается);
//    * никакого «Забыли пароль? → ссылка отправлена»: писем проект не
//      отправляет, поэтому честно «напишите нам».
//
// ⚠️ `/login` и `/register` ОСТАЮТСЯ рабочими: на них могут вести
//    внешние ссылки и закладки. Модалка — не единственный путь, а
//    основной в контексте страницы.
// ═══════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../hooks/useAuth.js';
import { useToast } from '../../../hooks/useToast.js';
import { consentAPI, CONSENT_TYPES } from '../../../api/consent.js';
import { getAnonymousId } from '../../../utils/anonymousId.js';
import { closeAuth, setLoginMode } from '../../../store/uiSlice.js';

// ============================================================
// ПОЛЕ ВВОДА В СТИЛЕ ВИТРИНЫ
// ============================================================
// ⚠️ Свой `label`+`input`, а не `ui/Input`: тот на Inter и на других
//    скруглениях — в модалке витрины он выглядел бы чужим (весь остальной
//    мир стикеров — Manrope). Связь `htmlFor`/`id` сохранена: без неё
//    клик по подписи не ставит курсор, а скринридер не читает название.
const Field = ({ id, label, hint, error, ...props }) => (
  <div style={{ marginBottom: 12 }}>
    <label
      htmlFor={id}
      style={{
        display: 'block',
        marginBottom: 6,
        fontSize: 12.5,
        fontWeight: 600,
        color: 'rgb(var(--c-text))',
        fontFamily: 'var(--font-stickers-body)',
      }}
    >
      {label}
    </label>
    <input
      id={id}
      style={{
        width: '100%',
        padding: '10px 14px',
        border: `1px solid ${error ? 'rgb(var(--c-danger))' : 'rgb(var(--c-border))'}`,
        borderRadius: 9,
        background: 'rgb(var(--c-surface))',
        color: 'rgb(var(--c-text))',
        fontSize: 13.5,
        fontFamily: 'var(--font-stickers-body)',
        outline: 'none',
        boxSizing: 'border-box',
      }}
      aria-invalid={error ? 'true' : undefined}
      {...props}
    />
    {hint && !error && (
      <div style={{ marginTop: 4, fontSize: 11.5, color: 'rgb(var(--c-muted))' }}>{hint}</div>
    )}
    {error && (
      <div style={{ marginTop: 4, fontSize: 11.5, color: 'rgb(var(--c-danger))' }}>{error}</div>
    )}
  </div>
);

const Tab = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-selected={active}
    style={{
      padding: '6px 14px',
      borderRadius: 7,
      fontSize: 12.5,
      fontWeight: active ? 700 : 600,
      color: active ? 'rgb(var(--c-text))' : 'rgb(var(--c-text-secondary))',
      background: active ? 'rgb(var(--c-surface))' : 'transparent',
      boxShadow: active ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'var(--font-stickers-body)',
    }}
  >
    {children}
  </button>
);

export const AuthModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((s) => s.ui.isLoginOpen);
  const mode = useSelector((s) => s.ui.loginMode);
  const { login, register, isAuthenticated, isLoading } = useAuth();
  const { success, error: toastError } = useToast();

  const isRegister = mode === 'register';

  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  // 🔑 ГАЛОЧКА ПО УМОЛЧАНИЮ ПУСТАЯ. Предустановленная отметка — это
  //    «согласие по умолчанию», которого не допускает ч. 1 ст. 9 152-ФЗ.
  const [consentPd, setConsentPd] = useState(false);

  // Открыли заново — форма чистая. Иначе после неудачного входа оставался
  // бы пароль от прошлого человека (модалка живёт в Layout постоянно).
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSubmitError('');
  }, [isOpen, mode]);

  const close = () => {
    dispatch(closeAuth());
    setForm({ email: '', password: '', first_name: '', last_name: '', phone: '' });
    setConsentPd(false);
    setErrors({});
    setSubmitError('');
  };

  // Esc закрывает — привычное поведение.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const next = {};
    if (!form.email) next.email = 'Укажите email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Похоже, это не email';
    if (!form.password) next.password = 'Укажите пароль';
    else if (form.password.length < 6) next.password = 'Не меньше 6 символов';
    if (isRegister) {
      if (!form.first_name.trim()) next.first_name = 'Укажите имя';
      if (!consentPd) next.consent = 'Нужно согласие на обработку персональных данных';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    try {
      if (isRegister) {
        const result = await register({
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
        });
        // 🔑 ПРОВЕРЯЕМ, ЧТО ВХОД ДЕЙСТВИТЕЛЬНО ПРОИЗОШЁЛ.
        //    Раньше регистрация «проходила», а токена не было — и человек
        //    оставался гостем. Теперь если токена нет, честно говорим
        //    об этом, а не закрываем модалку с «успех».
        if (!result?.payload?.access_token) {
          setSubmitError('Аккаунт создан, но вход не выполнился. Попробуйте войти.');
          return;
        }
        // Согласие фиксируем НА СЕРВЕРЕ: галочка в браузере ничего не
        // доказывает (оператор обязан доказать наличие согласия).
        try {
          await consentAPI.accept({
            consent_type: CONSENT_TYPES.PERSONAL_DATA,
            document_version: '1.0',
            anonymous_id: getAnonymousId(),
          });
        } catch (consentErr) {
          console.error('Не удалось зафиксировать согласие:', consentErr);
          toastError('Аккаунт создан, но согласие не сохранилось. Сообщите нам.');
        }
        success('Аккаунт создан');
      } else {
        const result = await login({ email: form.email, password: form.password });
        if (!result?.payload?.access_token) {
          setSubmitError('Не удалось войти');
          return;
        }
        success('Добро пожаловать!');
      }
      close();
    } catch (err) {
      const msg = typeof err === 'string' ? err : (err?.message || 'Произошла ошибка');
      setSubmitError(msg);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isRegister ? 'Регистрация' : 'Вход'}
      onClick={close}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(23,25,29,.38)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 452,
          maxWidth: '100%',
          maxHeight: '92vh',
          overflow: 'auto',
          background: 'rgb(var(--c-surface))',
          borderRadius: 24,
          boxShadow: '0 28px 70px rgba(0,0,0,.32)',
          fontFamily: 'var(--font-stickers-body)',
        }}
      >
        {/* ВКЛАДКИ */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
          <div style={{ display: 'flex', gap: 6, background: 'rgb(var(--c-surface-2))', padding: 3, borderRadius: 9 }}>
            <Tab active={isRegister} onClick={() => dispatch(setLoginMode('register'))}>Регистрация</Tab>
            <Tab active={!isRegister} onClick={() => dispatch(setLoginMode('login'))}>Вход</Tab>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Закрыть"
            style={{
              width: 26, height: 26, borderRadius: '50%', border: 'none',
              background: 'rgb(var(--c-surface-2))',
              color: 'rgb(var(--c-text-secondary))',
              fontSize: 13, cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        <h3
          style={{
            margin: '16px 24px 4px',
            fontFamily: 'var(--font-stickers-head)',
            fontSize: 21,
            fontWeight: 800,
            letterSpacing: '-.02em',
            color: 'rgb(var(--c-text))',
            lineHeight: 1.2,
          }}
        >
          {isRegister ? 'Создайте аккаунт, чтобы сохранить подборку' : 'Вход в личный кабинет'}
        </h3>
        <p style={{ margin: '0 24px 16px', fontSize: 12.5, color: 'rgb(var(--c-text-secondary))', lineHeight: 1.5 }}>
          {isRegister
            ? 'Ваша подборка и картинки останутся с вами. На печать отправить можно и без аккаунта.'
            : 'Здесь ваши заказы и сохранённые подборки.'}
        </p>

        {submitError && (
          <div
            style={{
              margin: '0 24px 12px',
              padding: '10px 12px',
              borderRadius: 9,
              background: 'rgb(var(--c-danger) / 0.1)',
              color: 'rgb(var(--c-danger))',
              fontSize: 12.5,
              lineHeight: 1.45,
            }}
          >
            {submitError}
          </div>
        )}

        <form onSubmit={submit} style={{ padding: '0 24px' }}>
          {isRegister && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <Field
                  id="auth-first-name" label="Имя" value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  error={errors.first_name} autoComplete="given-name"
                />
              </div>
              <div style={{ flex: 1 }}>
                <Field
                  id="auth-last-name" label="Фамилия" value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  autoComplete="family-name"
                />
              </div>
            </div>
          )}

          <Field
            id="auth-email" label="Email" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={errors.email} autoComplete="email" placeholder="you@example.com"
          />
          <Field
            id="auth-password" label="Пароль" type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            error={errors.password}
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            hint={isRegister ? 'Не меньше 6 символов' : undefined}
          />

          {isRegister && (
            <div style={{ marginTop: 2 }}>
              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 11.5, lineHeight: 1.5, color: 'rgb(var(--c-text-secondary))', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={consentPd}
                  onChange={(e) => setConsentPd(e.target.checked)}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <span>
                  Я согласен на обработку персональных данных в соответствии с{' '}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'rgb(var(--c-accent))', fontWeight: 600 }}>
                    политикой конфиденциальности
                  </Link>
                </span>
              </label>
              {errors.consent && (
                <div style={{ marginTop: 6, fontSize: 11.5, color: 'rgb(var(--c-danger))' }}>{errors.consent}</div>
              )}
            </div>
          )}

          <div style={{ padding: '16px 0 20px' }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: 9,
                padding: 12,
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                // 🔴 ИМЕННО `rgb(var(--c-accent))`, а НЕ класс `bg-accent`:
                //    этот класс не генерируется сборкой, и кнопка становится
                //    белой на белом (грабля 10-БИС, BRAND_BOOK).
                background: 'rgb(var(--c-accent))',
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? 'wait' : 'pointer',
                fontFamily: 'var(--font-stickers-body)',
              }}
            >
              {isRegister ? 'Создать аккаунт' : 'Войти'}
            </button>
            <div style={{ marginTop: 12, textAlign: 'center', fontSize: 11.5, color: 'rgb(var(--c-muted))' }}>
              {isRegister
                ? 'Вы останетесь на этой же странице — ничего не потеряется'
                : (
                  <span>
                    Забыли пароль? <b style={{ color: 'rgb(var(--c-text))' }}>Напишите нам</b> — восстановим вручную
                  </span>
                )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
